/**
 * Page de connexion - email + mot de passe.
 */

import { pocketbaseAuth } from '@services/pocketbase-auth';
import { html, nothing } from 'lit';

type LoginMode = 'signin' | 'signup';
type LoginState = 'idle' | 'loading' | { error: string };

let loginMode: LoginMode = 'signin';
let loginState: LoginState = 'idle';

export function renderLoginPage(onStateChange: () => void): unknown {
  async function handleSubmit(e: Event) {
    e.preventDefault();
    const form = e.target as HTMLFormElement;
    const email = (form.querySelector('#login-email') as HTMLInputElement)?.value?.trim();
    const password = (form.querySelector('#login-password') as HTMLInputElement)?.value;
    if (!email || !password) return;

    loginState = 'loading';
    onStateChange();

    try {
      if (loginMode === 'signup') {
        const displayName = (form.querySelector('#login-name') as HTMLInputElement)?.value?.trim();
        await pocketbaseAuth.signUp(email, password, displayName || undefined);
      } else {
        await pocketbaseAuth.signIn(email, password);
      }
      loginState = 'idle';
    } catch (err) {
      loginState = { error: err instanceof Error ? err.message : 'Erreur inconnue' };
    }
    onStateChange();
  }

  function toggleMode(e: Event) {
    e.preventDefault();
    loginMode = loginMode === 'signin' ? 'signup' : 'signin';
    loginState = 'idle';
    onStateChange();
  }

  const isSignUp = loginMode === 'signup';
  const isLoading = loginState === 'loading';

  return html`
    <div class="login-page">
      <div class="login-card">
        <h1 class="login-title">COF2</h1>
        <p class="login-subtitle">Chroniques Oubliees Fantasy</p>

        <div class="login-divider">
          <span>${isSignUp ? 'Creer un compte' : 'Entrer dans la partie'}</span>
        </div>

        <form @submit=${handleSubmit} class="login-form">
          ${
            isSignUp
              ? html`
            <label class="login-field">
              <span class="login-label">Nom d'aventurier</span>
              <input
                type="text"
                id="login-name"
                class="login-input"
                placeholder="Votre pseudo"
                ?disabled=${isLoading}
              />
            </label>
          `
              : nothing
          }

          <label class="login-field">
            <span class="login-label">Adresse email</span>
            <input
              type="email"
              id="login-email"
              class="login-input"
              placeholder="votre@email.com"
              required
              autofocus
              ?disabled=${isLoading}
            />
          </label>

          <label class="login-field">
            <span class="login-label">Mot de passe</span>
            <input
              type="password"
              id="login-password"
              class="login-input"
              placeholder=${isSignUp ? '8 caracteres minimum' : 'Votre mot de passe'}
              required
              minlength=${isSignUp ? 8 : 1}
              ?disabled=${isLoading}
            />
          </label>

          ${
            typeof loginState === 'object'
              ? html`<p class="login-error">${loginState.error}</p>`
              : nothing
          }

          <button
            type="submit"
            class="login-submit"
            ?disabled=${isLoading}
          >
            ${isLoading ? 'Chargement...' : isSignUp ? 'Forger mon compte' : 'Se connecter'}
          </button>
        </form>

        <p class="login-toggle">
          ${
            isSignUp
              ? html`Deja un compte ? <button class="login-toggle-link" @click=${toggleMode}>Se connecter</button>`
              : html`Pas encore de compte ? <button class="login-toggle-link" @click=${toggleMode}>Creer un compte</button>`
          }
        </p>
      </div>
    </div>
  `;
}
